import type { AdController, StormcloudVideoPlayerConfig, Scte35Marker } from "../types";

export interface AdTimingCallbacks {
  onAdStopTimerFired(): void;
  onAdStartTimerFired(marker: Scte35Marker, cueKey: string | undefined): void;
  onWatchdogTimeout(): void;
  onFailsafeTimeout(): void;
}

export class AdTimingService {
  private adStopTimerId: number | undefined;
  private adStartTimerId: number | undefined;
  private adFailsafeTimerId: number | undefined;
  private adRequestWatchdogId: number | undefined;
  private adRequestWatchdogToken: number | null = null;
  private adFailsafeToken: number | null = null;

  ptsDriftEmaMs = 0;
  expectedAdBreakDurationMs: number | undefined;
  currentAdBreakStartWallClockMs: number | undefined;
  scteAdBreakEndWallClockMs: number | undefined;
  adBreakPlayedDurationMs: number = 0;
  adBreakEpoch: number = 0;

  adRequestTokenCounter = 0;
  activeAdRequestToken: number | null = null;
  currentAdIndex: number = 0;
  totalAdsInBreak: number = 0;
  totalAdRequestsInBreak: number = 0;
  readonly maxTotalAdRequestsPerBreak: number = 10;
  lastAdRequestTime: number = 0;
  consecutiveFailures: number = 0;
  readonly maxConsecutiveFailures: number = 5;
  readonly minAdRequestIntervalMs: number = 3000;
  readonly backoffBaseMs: number = 1000;
  readonly maxBackoffMs: number = 15000;
  private readonly defaultMinRemainingMsToStartAd: number = 4000;

  failedVastUrls: Set<string> = new Set();
  temporaryFailureUrls: Map<string, number> = new Map();
  private readonly temporaryFailureCooldownMs: number = 30000;

  private globalConsecutiveNoFills: number = 0;
  private readonly globalNoFillThreshold: number = 3;
  private globalNoFillCooldownUntil: number = 0;
  private readonly globalNoFillBackoffBaseMs: number = 5000;
  private readonly globalNoFillBackoffMaxMs: number = 60000;
  private globalLastGamRequestTime: number = 0;
  private readonly globalMinGamIntervalMs: number = 3000;

  private config: StormcloudVideoPlayerConfig;
  private video: HTMLVideoElement;
  private callbacks: AdTimingCallbacks;

  constructor(
    config: StormcloudVideoPlayerConfig,
    video: HTMLVideoElement,
    callbacks: AdTimingCallbacks
  ) {
    this.config = config;
    this.video = video;
    this.callbacks = callbacks;
  }

  get debug(): boolean {
    return !!this.config.debugAdTiming;
  }

  // --- Ad stop timer ---

  scheduleAdStopCountdown(remainingMs: number): void {
    this.clearAdStopTimer();
    const ms = Math.max(0, Math.floor(remainingMs));
    if (ms === 0) {
      this.ensureAdStoppedByTimer();
      return;
    }
    this.adStopTimerId = window.setTimeout(() => {
      this.ensureAdStoppedByTimer();
    }, ms) as unknown as number;
  }

  clearAdStopTimer(): void {
    if (this.adStopTimerId != null) {
      clearTimeout(this.adStopTimerId);
      this.adStopTimerId = undefined;
    }
  }

  private ensureAdStoppedByTimer(): void {
    this.adStopTimerId = undefined;
    this.callbacks.onAdStopTimerFired();
  }

  // --- Ad start timer ---

  scheduleAdStartIn(
    delayMs: number,
    marker: Scte35Marker = { type: "start" } as Scte35Marker,
    cueKey?: string
  ): void {
    this.clearAdStartTimer();
    const ms = Math.max(0, Math.floor(delayMs));
    if (ms === 0) {
      this.callbacks.onAdStartTimerFired(marker, cueKey);
      return;
    }
    this.adStartTimerId = window.setTimeout(() => {
      this.callbacks.onAdStartTimerFired(marker, cueKey);
    }, ms) as unknown as number;
  }

  clearAdStartTimer(): void {
    if (this.adStartTimerId != null) {
      clearTimeout(this.adStartTimerId);
      this.adStartTimerId = undefined;
    }
  }

  // --- PTS drift ---

  updatePtsDrift(ptsSecondsSample: number): void {
    const sampleMs = (this.video.currentTime - ptsSecondsSample) * 1000;
    if (!Number.isFinite(sampleMs) || Math.abs(sampleMs) > 60000) return;
    const alpha = 0.1;
    this.ptsDriftEmaMs = this.ptsDriftEmaMs * (1 - alpha) + sampleMs * alpha;
  }

  resolveScteBreakStartWallClockMs(marker: Scte35Marker): number {
    const nowWallClockMs = Date.now();
    if (typeof marker.ptsSeconds !== "number") {
      return nowWallClockMs;
    }

    const nowMs = this.video.currentTime * 1000;
    const estimatedCurrentPtsMs = nowMs - this.ptsDriftEmaMs;
    const deltaMs = marker.ptsSeconds * 1000 - estimatedCurrentPtsMs;
    if (!Number.isFinite(deltaMs)) {
      return nowWallClockMs;
    }

    return nowWallClockMs + Math.floor(deltaMs);
  }

  // --- Duration boundary ---

  setAdBreakDurationBoundary(durationMs: number | undefined): void {
    this.expectedAdBreakDurationMs = durationMs;
    if (
      durationMs != null &&
      this.currentAdBreakStartWallClockMs != null
    ) {
      this.scteAdBreakEndWallClockMs =
        this.currentAdBreakStartWallClockMs + durationMs;
    } else {
      this.scteAdBreakEndWallClockMs = undefined;
    }
  }

  scheduleAdStopAtBreakBoundary(): void {
    if (this.expectedAdBreakDurationMs == null) {
      return;
    }
    this.scheduleAdStopCountdown(this.getWallClockRemainingAdMs());
  }

  // --- Duration budget ---

  getWallClockRemainingAdMs(): number {
    const endWallClockMs = this.getAdBreakEndWallClockMs();
    if (endWallClockMs != null) {
      return Math.max(0, endWallClockMs - Date.now());
    }
    return 0;
  }

  getAdBreakEndWallClockMs(): number | undefined {
    if (this.scteAdBreakEndWallClockMs != null) {
      return this.scteAdBreakEndWallClockMs;
    }
    if (
      this.expectedAdBreakDurationMs == null ||
      this.currentAdBreakStartWallClockMs == null
    ) {
      return undefined;
    }
    return this.currentAdBreakStartWallClockMs + this.expectedAdBreakDurationMs;
  }

  getDurationBudgetRemainingMs(): number {
    if (this.expectedAdBreakDurationMs == null) {
      return 0;
    }
    return Math.max(0, this.expectedAdBreakDurationMs - this.adBreakPlayedDurationMs);
  }

  getLoadedAdDurationMs(adController: AdController): number {
    const durationSec = adController.getLoadedAdDuration?.();
    if (durationSec == null || durationSec <= 0) {
      return 0;
    }
    return Math.round(durationSec * 1000);
  }

  adFitsRemainingBudget(durationMs: number): boolean {
    if (durationMs <= 0) {
      return true;
    }
    return durationMs <= this.getDurationBudgetRemainingMs();
  }

  logAdDurationDecision(
    accepted: boolean,
    durationMs: number,
    adController: AdController
  ): void {
    if (!this.debug) {
      return;
    }
    const remainingBudget = this.getDurationBudgetRemainingMs();
    const adId = adController.getLoadedAdId?.() ?? "unknown";
    if (accepted) {
      console.log(
        `[AD-DURATION] Accepted ad id=${adId}, duration=${durationMs}ms, budget remaining after play=${Math.max(0, remainingBudget - durationMs)}ms`
      );
    } else {
      console.log(
        `[AD-DURATION] Rejected ad id=${adId}, duration=${durationMs}ms exceeds remaining budget=${remainingBudget}ms — requesting another ad`
      );
    }
  }

  recordPlayedAdDuration(adController: AdController): void {
    const durationMs = this.getLoadedAdDurationMs(adController);
    if (durationMs <= 0) {
      return;
    }
    this.adBreakPlayedDurationMs += durationMs;
    if (this.debug) {
      console.log(
        `[AD-DURATION] Played ${durationMs}ms — total played=${this.adBreakPlayedDurationMs}ms / ${this.expectedAdBreakDurationMs ?? "?"}ms`
      );
    }
  }

  ensureLoadedAdFitsBudget(adController: AdController): boolean {
    const durationMs = this.getLoadedAdDurationMs(adController);
    const fits = this.adFitsRemainingBudget(durationMs);
    this.logAdDurationDecision(fits, durationMs, adController);
    return fits;
  }

  rejectLoadedAdForDuration(adController: AdController): void {
    try {
      adController.destroy();
    } catch {}
  }

  markAdStarted(): void {
    this.currentAdIndex++;
    this.totalAdsInBreak = this.currentAdIndex;
    this.totalAdRequestsInBreak++;
    this.lastAdRequestTime = Date.now();
  }

  notePodAdStarted(): void {
    this.currentAdIndex++;
    this.totalAdsInBreak = this.currentAdIndex;
  }

  getRemainingAdMs(): number {
    const wallClockRemaining = this.getWallClockRemainingAdMs();
    const budgetRemaining = this.getDurationBudgetRemainingMs();
    if (this.expectedAdBreakDurationMs == null) {
      return wallClockRemaining;
    }
    return Math.min(wallClockRemaining, budgetRemaining);
  }

  getMinRemainingMsToStartAd(): number {
    const configured = this.config.minRemainingMsToStartAd;
    if (typeof configured === "number" && configured >= 0) {
      return configured;
    }
    return this.defaultMinRemainingMsToStartAd;
  }

  hasTimeToStartAnotherAd(): boolean {
    const minRemaining = this.getMinRemainingMsToStartAd();
    return (
      this.getWallClockRemainingAdMs() > minRemaining &&
      this.getDurationBudgetRemainingMs() > minRemaining
    );
  }

  // --- Circuit breaker ---

  isTemporaryAdError(error: Error): boolean {
    const message = error?.message?.toLowerCase() || "";

    if (message.includes("no ads available") || message.includes("no fill") || message.includes("nofill")) {
      return true;
    }
    if (message.includes("timeout")) {
      return true;
    }
    if (message.includes("rate limit") || message.includes("too many requests")) {
      return true;
    }
    if (message.includes("ad playback error")) {
      return true;
    }
    return false;
  }

  isUrlInCooldown(vastTagUrl: string): boolean {
    const failureTime = this.temporaryFailureUrls.get(vastTagUrl);
    if (failureTime == null) {
      return false;
    }

    const elapsed = Date.now() - failureTime;
    if (elapsed >= this.temporaryFailureCooldownMs) {
      this.temporaryFailureUrls.delete(vastTagUrl);
      return false;
    }

    return true;
  }

  cleanupTemporaryFailures(): void {
    const now = Date.now();
    for (const [url, timestamp] of this.temporaryFailureUrls) {
      if (now - timestamp >= this.temporaryFailureCooldownMs) {
        this.temporaryFailureUrls.delete(url);
      }
    }
  }

  isGamInCooldown(): boolean {
    return Date.now() < this.globalNoFillCooldownUntil;
  }

  recordGamNoFill(): void {
    this.globalConsecutiveNoFills++;

    if (this.globalConsecutiveNoFills >= this.globalNoFillThreshold) {
      const exponent = this.globalConsecutiveNoFills - this.globalNoFillThreshold;
      const backoff = Math.min(
        this.globalNoFillBackoffBaseMs * Math.pow(2, exponent),
        this.globalNoFillBackoffMaxMs
      );
      this.globalNoFillCooldownUntil = Date.now() + backoff;

      if (this.debug) {
        console.log(
          `[CIRCUIT-BREAKER] GAM cooldown activated: ${backoff}ms (${this.globalConsecutiveNoFills} consecutive no-fills)`
        );
      }
    }
  }

  resetGamNoFillCounter(): void {
    if (this.globalConsecutiveNoFills > 0 && this.debug) {
      console.log(
        `[CIRCUIT-BREAKER] Resetting no-fill counter (was ${this.globalConsecutiveNoFills})`
      );
    }
    this.globalConsecutiveNoFills = 0;
    this.globalNoFillCooldownUntil = 0;
  }

  async enforceGlobalRateLimit(): Promise<void> {
    const now = Date.now();
    const elapsed = now - this.globalLastGamRequestTime;
    if (elapsed < this.globalMinGamIntervalMs) {
      const waitMs = this.globalMinGamIntervalMs - elapsed;
      if (this.debug) {
        console.log(`[RATE-LIMIT] Waiting ${waitMs}ms before next GAM request`);
      }
      await new Promise(resolve => setTimeout(resolve, waitMs));
    }
    this.globalLastGamRequestTime = Date.now();
  }

  // --- Watchdog ---

  startAdRequestWatchdog(token: number): void {
    this.clearAdRequestWatchdog();

    const timeoutMs = this.config.adFailsafeTimeoutMs ?? 10000;
    this.adRequestWatchdogToken = token;
    this.adRequestWatchdogId = window.setTimeout(() => {
      if (this.adRequestWatchdogToken !== token) {
        return;
      }

      this.adRequestWatchdogId = undefined;
      this.adRequestWatchdogToken = null;
      if (this.activeAdRequestToken === token) {
        this.activeAdRequestToken = null;
      }

      this.logAdState("ad_request_timeout", { token, timeoutMs });
      this.callbacks.onWatchdogTimeout();
    }, timeoutMs) as unknown as number;

    this.logAdState("ad_request_watchdog_started", { token, timeoutMs });
  }

  clearAdRequestWatchdog(): void {
    if (this.adRequestWatchdogId != null) {
      clearTimeout(this.adRequestWatchdogId);
      this.adRequestWatchdogId = undefined;
    }

    if (this.adRequestWatchdogToken != null) {
      this.logAdState("ad_request_watchdog_cleared", {
        token: this.adRequestWatchdogToken,
      });
      this.adRequestWatchdogToken = null;
    }
  }

  // --- Failsafe ---

  startAdFailsafeTimer(token: number): void {
    this.clearAdFailsafeTimer();

    const failsafeMs = this.config.adFailsafeTimeoutMs ?? 10000;
    this.adFailsafeToken = token;

    this.adFailsafeTimerId = window.setTimeout(() => {
      if (this.adFailsafeToken !== token) {
        return;
      }

      this.adFailsafeTimerId = undefined;
      this.adFailsafeToken = null;

      if (this.activeAdRequestToken === token) {
        this.activeAdRequestToken = null;
      }

      this.logAdState("ad_failsafe_triggered", {
        token,
        failsafeMs,
        videoPaused: this.video.paused,
      });

      this.callbacks.onFailsafeTimeout();
    }, failsafeMs) as unknown as number;

    this.logAdState("ad_failsafe_started", { token, failsafeMs });
  }

  clearAdFailsafeTimer(): void {
    if (this.adFailsafeTimerId != null) {
      clearTimeout(this.adFailsafeTimerId);
      this.logAdState("ad_failsafe_cleared", { token: this.adFailsafeToken });
      this.adFailsafeTimerId = undefined;
    }

    this.adFailsafeToken = null;
  }

  logAdState(event: string, extra: Record<string, unknown> = {}, adPlayer?: AdController, inAdBreak?: boolean, showAds?: boolean): void {
    if (!this.debug) {
      return;
    }

    console.log("[StormcloudVideoPlayer][AdState]", {
      event,
      timestamp: new Date().toISOString(),
      showAds: showAds ?? false,
      adPlaying: adPlayer?.isAdPlaying() ?? false,
      inAdBreak: inAdBreak ?? false,
      activeAdRequestToken: this.activeAdRequestToken,
      ...extra,
    });
  }

  // --- Reset for ad break end ---

  resetForBreakEnd(): void {
    this.clearAdStopTimer();
    this.clearAdStartTimer();
    this.clearAdRequestWatchdog();
    this.clearAdFailsafeTimer();
    this.activeAdRequestToken = null;
    this.expectedAdBreakDurationMs = undefined;
    this.currentAdBreakStartWallClockMs = undefined;
    this.scteAdBreakEndWallClockMs = undefined;
    this.adBreakPlayedDurationMs = 0;
    this.currentAdIndex = 0;
    this.totalAdsInBreak = 0;
    this.consecutiveFailures = 0;
    this.globalConsecutiveNoFills = 0;
    this.adBreakEpoch++;
  }

  destroy(): void {
    this.clearAdStopTimer();
    this.clearAdStartTimer();
    this.clearAdFailsafeTimer();
    this.clearAdRequestWatchdog();
  }
}
